import type {
  CaseManifestV1,
  DialogConditionV1,
  InterrogationNodeV1,
  NpcInstanceV1,
} from "@cold-case/shared/case-manifest";

import {
  clueDisplayName,
  npcDisplayName,
  npcInstanceDisplayName,
} from "./manifest-display.js";
import { resolveBuildingDisplayName, zoneDisplayName } from "./building-display.js";
import {
  dialogConditionMet,
  substituteDialog,
  type CoherenceJournal,
} from "./dialog-parser.js";

export type NpcCaseNotes = {
  subjectName: string;
  personality: string;
};

/** Player-facing personality blurb from manifest (`personality` or `personality_brief`). */
export function npcPersonalityText(npc: NpcInstanceV1): string {
  const raw =
    (npc as { personality?: string }).personality?.trim() ||
    npc.personality_brief?.trim() ||
    "";
  return raw || "No background on file yet — learn who they are by talking.";
}

/** Never reveal killer role to the player before accusation. */
export function npcRoleLabelForPlayer(role: NpcInstanceV1["role"]): string {
  switch (role) {
    case "witness":
      return "Witness";
    case "red_herring":
      return "Person of interest";
    case "ambient":
      return "Local contact";
    case "filler":
      return "Local";
    case "killer":
      return "Person of interest";
    default:
      return "Interview subject";
  }
}

export function npcConnectionBlurb(npc: NpcInstanceV1, manifest: CaseManifestV1): string {
  const zone = zoneDisplayName(manifest, npc.zone);
  const nearId = npc.position_near_building?.trim();
  const near = nearId ? resolveBuildingDisplayName(manifest, nearId) : null;
  const victim = manifest.victim.name;
  const where = near ? `around ${near} in ${zone}` : `on the streets of ${zone}`;
  switch (npc.role) {
    case "witness":
      return `Seen ${where}. May know what happened to ${victim}.`;
    case "red_herring":
      return `Connected ${near ? `to ${near} in ${zone}` : `around ${zone}`}. Their story may not line up — still worth a conversation.`;
    case "ambient":
      return near
        ? `Works or spends time near ${near} in ${zone}. Could know the neighborhood rhythm around the murder.`
        : `Often out in ${zone}. Could know the neighborhood rhythm around the murder.`;
    case "filler":
      return near
        ? `A ${zone} local often seen near ${near}. Unlikely to hold case-breaking evidence.`
        : `A ${zone} local who spends time on the streets. Unlikely to hold case-breaking evidence.`;
    case "killer":
      return `Tied ${near ? `to ${near} in ${zone}` : `to ${zone}`}. Treat like any other lead until the evidence says otherwise.`;
    default:
      return near ? `Located near ${near} in ${zone}.` : `Patrols ${zone}.`;
  }
}

function explainConditionRequirement(
  c: DialogConditionV1,
  manifest: CaseManifestV1
): string {
  const t = String(c.target ?? "");
  switch (c.type) {
    case "has_clue": {
      return manifest.clues.some((x) => x.clue_id === t)
        ? `Find “${clueDisplayName(manifest, t)}” in the world or your Case File before this line of questioning opens up.`
        : "Gather more evidence in the field before this topic will pay off.";
    }
    case "talked_to": {
      return manifest.npcs.some((n) => n.instance_id === t)
        ? `Speak with ${npcDisplayName(manifest, t)} first — their account may unlock a fuller answer here.`
        : "Interview someone else connected to the case first.";
    }
    case "visited_building": {
      const place = resolveBuildingDisplayName(manifest, t);
      return `Search ${place} before you can reference what you found there.`;
    }
    case "player_count_gte": {
      const n = Number(t);
      return Number.isFinite(n) && n > 1
        ? `This case needs at least ${n} detectives present to pursue this thread.`
        : "More detectives on the case may unlock this topic.";
    }
    case "all_players_present":
      return "All detectives should be here before you press this point.";
    default:
      return "Further investigation may unlock a better answer.";
  }
}

export function explainQuestionTopic(
  node: InterrogationNodeV1,
  journal: CoherenceJournal,
  playerCount: number,
  manifest: CaseManifestV1,
  names: string[],
  victimName: string
): string {
  const c = node.condition;
  if (!c) {
    return "A standard line of inquiry — you can ask this anytime.";
  }
  const met = dialogConditionMet(c, journal, playerCount);
  if (met) {
    return "You already have enough context — they should answer more fully.";
  }
  if (c.locked_hint?.trim()) {
    return substituteDialog(c.locked_hint.trim(), names, victimName);
  }
  return explainConditionRequirement(c, manifest);
}

export function buildNpcCaseNotes(npc: NpcInstanceV1): NpcCaseNotes {
  return {
    subjectName: `Case Profile: ${npcInstanceDisplayName(npc)}`,
    personality: npcPersonalityText(npc),
  };
}
